.page {
  max-width: 960px;
  margin: 0 auto;
  padding: 32px 28px 48px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.pageHeader {
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: fade-up 0.5s var(--kh-ease-out) both;
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color var(--kh-duration-fast) var(--kh-ease-out);
}

.backLink:hover {
  color: rgba(255, 255, 255, 0.9);
}

.headerTitle {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolName {
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.02em;
}

.badge {
  font-family: var(--kh-font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.7);
  border-radius: var(--kh-radius-badge);
  padding: 2px 8px;
}

/* Summary filter chips */

.summary {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  animation: fade-up 0.5s var(--kh-ease-out) 0.05s both;
}

.summaryChip {
  font-family: var(--kh-font-mono);
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 0.5px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;
  transition: all var(--kh-duration-fast) var(--kh-ease-out);
}

.summaryChip:hover {
  background: rgba(255, 255, 255, 0.28);
  border-color: rgba(255, 255, 255, 0.6);
}

.summaryChipActive {
  background: #fff;
  color: var(--kh-text-primary);
  border-color: #fff;
}

.summaryChipHigh {
  composes: summaryChip;
}

.summaryChipMedium {
  composes: summaryChip;
}

.summaryChipLow {
  composes: summaryChip;
}

/* Pair cards */

.pairList {
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: fade-up 0.5s var(--kh-ease-out) 0.1s both;
}

.pairCard {
  background: var(--kh-surface);
  border: 0.5px solid var(--kh-border);
  border-radius: var(--kh-radius-card);
  box-shadow: var(--kh-shadow-1);
  overflow: hidden;
}

/* Pair header — confidence + score + shared context */

.pairHeader {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 0.5px solid var(--kh-border);
}

.confidenceBadge {
  font-family: var(--kh-font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 3px 10px;
  border-radius: var(--kh-radius-badge);
}

.confidenceHigh {
  background: rgba(237, 25, 67, 0.12);
  color: var(--kh-red-dark);
}

.confidenceMedium {
  background: var(--kh-amber-dim);
  color: #9a6b00;
}

.confidenceLow {
  background: var(--kh-surface-2);
  color: var(--kh-text-muted);
}

.pairScore {
  font-family: var(--kh-font-mono);
  font-size: 12px;
  color: var(--kh-text-muted);
}

.sharedContext {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: var(--kh-text-secondary);
}

.sharedContextItem {
  display: flex;
  align-items: center;
  gap: 5px;
}

.sharedContextLabel {
  color: var(--kh-text-muted);
}

/* Reasons row */

.pairReasons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 20px;
  border-bottom: 0.5px solid var(--kh-border);
  background: var(--kh-surface-2);
}

.reasonTag {
  font-size: 11px;
  font-family: var(--kh-font-mono);
  color: var(--kh-text-secondary);
  background: var(--kh-surface);
  border: 0.5px solid var(--kh-border);
  padding: 2px 8px;
  border-radius: 4px;
}

/* Two-column entries: keep (left) vs suspect (right) */

.pairEntries {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
}

/* Column labels */

.columnLabel {
  font-family: var(--kh-font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: var(--kh-radius-badge);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.keepLabel {
  composes: columnLabel;
  background: var(--kh-teal-dim);
  color: var(--kh-teal);
}

.suspectLabel {
  composes: columnLabel;
  background: var(--kh-red-dim);
  color: var(--kh-red-dark);
}

/* Entry cards — shared base */

.entryCard {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.entryKeep {
  composes: entryCard;
  border-top: 2px solid var(--kh-teal);
}

.entrySuspect {
  composes: entryCard;
  border-top: 2px solid var(--kh-red);
  border-left: 0.5px solid var(--kh-border);
}

.entryLabelRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.entryDesc {
  font-size: 14px;
  font-weight: 600;
  color: var(--kh-text-primary);
  word-break: break-word;
  line-height: 1.4;
}

.entryMeta {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.entryMetaRow {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  padding: 2px 0;
  border-radius: 3px;
}

.entryMetaLabel {
  color: var(--kh-text-muted);
}

.entryMetaValue {
  color: var(--kh-text-secondary);
  text-align: right;
}

.billableYes {
  color: var(--kh-teal);
  font-weight: 600;
}

/* Highlight rows where the two entries differ */

.diffHighlight {
  background: rgba(204, 140, 26, 0.08);
  margin: 0 -6px;
  padding: 2px 6px;
  border-radius: 3px;
}

/* Delete button — full-width, prominent, only on suspect side */

.deleteBtnFull {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 4px;
  font-family: var(--kh-font-mono);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 10px 16px;
  border: 0.5px solid var(--kh-red-border);
  border-radius: var(--kh-radius-sm);
  background: var(--kh-red-dim);
  color: var(--kh-red-dark);
  cursor: pointer;
  transition:
    background var(--kh-duration-fast) var(--kh-ease-out),
    border-color var(--kh-duration-fast) var(--kh-ease-out);
}

.deleteBtnFull:hover {
  background: rgba(237, 25, 67, 0.2);
  border-color: var(--kh-red);
}

.deleteBtnFull:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Swap button — subtle, lets user swap keep/suspect sides */

.swapBtn {
  background: none;
  border: none;
  color: var(--kh-text-muted);
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  transition: color var(--kh-duration-fast);
}

.swapBtn:hover {
  color: var(--kh-text-primary);
}

/* Empty / loading / error states */

.emptyState {
  text-align: center;
  padding: 64px 24px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 15px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 2.5px solid rgba(255, 255, 255, 0.15);
  border-top-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  margin: 0 auto 16px;
  animation: spin 0.8s linear infinite;
}

.errorMsg {
  background: var(--kh-surface);
  border: 1px solid var(--kh-red-border);
  border-radius: var(--kh-radius-card);
  padding: 14px 20px;
  color: var(--kh-red-dark);
  font-size: 13px;
  box-shadow: var(--kh-shadow-1);
}

.refreshBtn {
  font-family: var(--kh-font-mono);
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 0.5px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;
  transition:
    background var(--kh-duration-fast),
    border-color var(--kh-duration-fast);
}

.refreshBtn:hover {
  background: rgba(255, 255, 255, 0.28);
  border-color: rgba(255, 255, 255, 0.6);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fade-up {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 640px) {
  .pairEntries {
    grid-template-columns: 1fr;
  }
  .entrySuspect {
    border-left: none;
    border-top: 2px solid var(--kh-red);
  }
  .sharedContext {
    display: none;
  }
}
